<template>
  <div class="page">
    <!-- Pipeline Bar -->
    <div class="pipelineShell card">
      <div class="pipelineTop">
        <div>
          <div class="sectionKicker">Sales Quotations</div>
          <div class="pipelineTitle">Quotation Pipeline</div>
          <div class="sectionSub">
            Track versions, finalize the customer-approved quotation, and create the order only after signed confirmation.
          </div>
        </div>

        <div class="pipelineStats">
          <div class="statChip">
            <div class="statChipLabel">Inquiry Docs</div>
            <div class="statChipValue">{{ docsStateLabel }}</div>
          </div>
          <div class="statChip">
            <div class="statChipLabel">Versions</div>
            <div class="statChipValue">{{ versions.length }}</div>
          </div>
          <div class="statChip">
            <div class="statChipLabel">Signed Final</div>
            <div class="statChipValue">{{ signedFinalVersion ? ("v" + signedFinalVersion.version) : "None" }}</div>
          </div>
        </div>
      </div>

      <div class="pipelineRow">
        <div
          v-for="(step, index) in pipelineSteps"
          :key="step.key"
          class="pipeStep"
          :class="pipeStepClass(step)"
        >
          <div class="pipeIndex">
            <span v-if="step.state === 'done'">✓</span>
            <span v-else>{{ index + 1 }}</span>
          </div>

          <div class="pipeBody">
            <div class="pipeLabel">{{ step.label }}</div>
            <div class="pipeHint">{{ step.hint }}</div>
          </div>

          <div v-if="index < pipelineSteps.length - 1" class="pipeConnector"></div>
        </div>
      </div>
    </div>

    <!-- Banner -->
    <div v-if="bannerMessage" class="banner" :class="'banner--' + bannerType">
      <div class="bannerText">{{ bannerMessage }}</div>
      <button class="bannerClose" type="button" @click="clearBanner">×</button>
    </div>

    <!-- Inquiry Picker -->
    <div class="card">
      <div class="cardHead">
        <div>
          <div class="sectionKicker">Select Client</div>
          <div class="sectionTitle">Ready inquiries for quotation</div>
          <div class="sectionSub">Pick a customer whose inquiry documents are complete.</div>
        </div>

        <button class="btn btn--ghost" @click="fetchReady()" :disabled="loadingReady">
          {{ loadingReady ? "Refreshing…" : "Refresh list" }}
        </button>
      </div>

      <div class="pickerGrid">
        <div class="field">
          <label class="label">Search</label>
          <input
            class="inp"
            v-model="readySearch"
            @input="debouncedFetchReady()"
            :disabled="loadingReady"
            placeholder="Search client / phone / lead code / inquiry id…"
          />
        </div>

        <div class="field">
          <label class="label">Client / Inquiry</label>
          <select
            class="inp"
            v-model="selectedInquiryId"
            @change="onSelectInquiry()"
            :disabled="loadingReady"
          >
            <option value="">Select client…</option>
            <option v-for="r in readyList" :key="r.inquiryId" :value="String(r.inquiryId)">
              {{ r.customerName }} • {{ r.customerPhone || "—" }} • {{ r.leadCode }} • Inquiry #{{ r.inquiryId }}
            </option>
          </select>
        </div>
      </div>

      <div v-if="selectedReady" class="summaryPills summaryPills--top">
        <span class="softPill"><b>{{ selectedReady.customerName }}</b></span>
        <span class="softPill">{{ selectedReady.customerPhone || "—" }}</span>
        <span class="softPill">{{ selectedReady.leadCode }}</span>
        <span class="softPill">Owner: {{ selectedReady.ownerName }}</span>
        <span class="softPill">Last version: v{{ selectedReady.maxVersion }}</span>
      </div>
    </div>

    <!-- Main Workspace -->
    <div class="workspace">
      <!-- Left / Main -->
      <div class="workspaceMain">
        <!-- Active Action Card -->
        <div class="card activeCard" :class="'activeCard--' + currentStageKey">
          <div class="activeCardTop">
            <div>
              <div class="sectionKicker">Current Focus</div>
              <div class="activeTitle">{{ activeActionTitle }}</div>
              <div class="sectionSub">{{ activeActionSub }}</div>
            </div>

            <div class="activeStatus">
              <span class="focusTag" :class="'focusTag--' + currentStageKey">{{ currentStageLabel }}</span>
            </div>
          </div>

          <div class="activeMeta" v-if="inquiryId">
            <span class="softPill"><b>{{ inquiry && inquiry.customerName ? inquiry.customerName : "Selected inquiry" }}</b></span>
            <span class="softPill">Inquiry #{{ inquiryId }}</span>
            <span v-if="latestVersion" class="softPill">Latest version: <b>v{{ latestVersion.version }}</b></span>
            <span v-if="signedFinalVersion" class="softPill softPill--good">Signed final: <b>v{{ signedFinalVersion.version }}</b></span>
          </div>

          <div class="actionStrip">
            <button
              v-if="showPrimaryCreateVersion"
              class="btn btn--primaryStrong"
              @click="createNewVersion"
              :disabled="loading || !canCreateVersion"
            >
              Create First Version
            </button>

            <button
              v-if="showPrimaryGenerate"
              class="btn btn--primaryStrong"
              @click="toggleTentativePanelForPrimary"
              :disabled="loading || !selectedVersionObj || selectedVersionObj.hasQuotePdf || selectedVersionObj.status === 'signed_final'"
            >
              {{ showTentativePanel ? "Hide Draft Builder" : "Generate Tentative Draft" }}
            </button>

            <button
              v-if="showPrimaryMarkSent"
              class="btn btn--primaryStrong"
              @click="markSent(selectedVersionObj)"
              :disabled="loading || !canMarkSent"
            >
              Confirm Sent to Client
            </button>

            <button
              v-if="showPrimarySign"
              class="btn btn--primaryStrong"
              @click="focusSignedSection"
              :disabled="loading || !selectedVersionObj || !selectedVersionObj.hasQuotePdf || selectedVersionObj.hasSignedFinal || selectedVersionObj.status === 'signed_final'"
            >
              Upload Signed Final
            </button>

            <button
              v-if="showPrimaryOrder"
              class="btn btn--primaryStrong"
              @click="focusOrderSection"
              :disabled="loading || !canPlaceOrder"
            >
              Complete Order Placement
            </button>

            <button class="btn btn--ghost" @click="refreshAll" :disabled="loading || !inquiryId">
              {{ loading ? "Refreshing…" : "Refresh" }}
            </button>

            <button class="btn btn--ghost" @click="goToInquiryAttachments" :disabled="loading || !leadId">
              View Inquiry Attachments
            </button>

            <button
              v-if="showSecondaryCreateVersion"
              class="btn btn--ghost"
              @click="createNewVersion"
              :disabled="loading || !canCreateVersion"
            >
              + New Version
            </button>
          </div>

          <div v-if="activeActionReason" class="helperLine">{{ activeActionReason }}</div>
          <div v-else-if="inquiryId" class="helperLine helperLine--good">{{ activeActionGoodLine }}</div>
        </div>

        <!-- Version Hub -->
        <div v-if="versions.length" class="card">
          <div class="cardHead cardHead--tight">
            <div>
              <div class="sectionKicker">Version Hub</div>
              <div class="sectionTitle">Quotation versions</div>
              <div class="sectionSub">
                Keep every draft and final quotation visible so the full customer journey stays documented.
              </div>
            </div>
          </div>

          <div class="versionRail">
            <button
              v-for="v in sortedVersionsDesc"
              :key="v.id"
              type="button"
              class="versionPill"
              :class="versionPillClass(v)"
              @click="selectVersion(v)"
            >
              <div class="versionPillTop">
                <span class="versionPillNum">v{{ v.version }}</span>
                <span class="versionPillStatus">{{ statusLabel(v) }}</span>
              </div>
              <div class="versionPillSub">
                {{ v.createdAt ? prettyDTShort(v.createdAt) : "—" }}
              </div>
            </button>
          </div>

          <div v-if="selectedVersionObj" class="versionCardPro">
            <div class="versionCardHead">
              <div>
                <div class="versionHeroRow">
                  <div class="versionNum">Version v{{ selectedVersionObj.version }}</div>

                  <span class="badge" :class="badgeClass(selectedVersionObj)">
                    {{ statusLabel(selectedVersionObj) }}
                  </span>

                  <span
                    class="miniTag"
                    :class="selectedVersionObj.hasQuotePdf ? 'miniTag--ok' : 'miniTag--muted'"
                  >
                    Quote: {{ selectedVersionObj.hasQuotePdf ? "Attached" : "Pending" }}
                  </span>

                  <span
                    v-if="selectedVersionObj.hasQuotePdf"
                    class="miniTag"
                    :class="selectedVersionObj.isTentativeGenerated ? 'miniTag--warn' : 'miniTag--info'"
                  >
                    {{ selectedVersionObj.isTentativeGenerated ? "Tentative Draft" : "Uploaded Quotation" }}
                  </span>

                  <span
                    class="miniTag"
                    :class="selectedVersionObj.hasSignedFinal ? 'miniTag--ok' : 'miniTag--muted'"
                  >
                    Signed: {{ selectedVersionObj.hasSignedFinal ? "Attached" : "Pending" }}
                  </span>
                </div>

                <div class="sectionSub" v-if="selectedVersionObj.createdAt">
                  Created: {{ prettyDT(selectedVersionObj.createdAt) }}
                </div>
              </div>

              <div class="versionHeaderActions">
                <button
                  class="btn btn--ghost"
                  @click="downloadQuote(selectedVersionObj)"
                  :disabled="downloading || !selectedVersionObj.hasQuotePdf"
                >
                  Download Quote
                </button>

                <button
                  class="btn btn--ghost"
                  @click="downloadSigned(selectedVersionObj)"
                  :disabled="downloading || !selectedVersionObj.hasSignedFinal"
                >
                  Download Signed
                </button>
              </div>
            </div>

            <div class="timeline timeline--pro">
              <div class="timelineItem">
                <div class="tlDot on"></div>
                <div class="tlBody">
                  <div class="tlTitle">Created</div>
                  <div class="tlSub mono">{{ selectedVersionObj.createdAt ? prettyDT(selectedVersionObj.createdAt) : "—" }}</div>
                </div>
              </div>

              <div class="timelineItem">
                <div class="tlDot" :class="{ on: selectedVersionObj.hasQuotePdf }"></div>
                <div class="tlBody">
                  <div class="tlTitle">Quotation Ready</div>
                  <div class="tlSub mono">
                    {{
                      selectedVersionObj.hasQuotePdf
                        ? (selectedVersionObj.isTentativeGenerated ? "Tentative draft attached" : "Uploaded quotation attached")
                        : "Pending"
                    }}
                  </div>
                </div>
              </div>

              <div class="timelineItem">
                <div class="tlDot" :class="{ on: selectedVersionObj.status === 'sent' || selectedVersionObj.status === 'signed_final' }"></div>
                <div class="tlBody">
                  <div class="tlTitle">Sent to client</div>
                  <div class="tlSub mono">{{ selectedVersionObj.sentAt ? prettyDT(selectedVersionObj.sentAt) : "—" }}</div>
                </div>
              </div>

              <div class="timelineItem">
                <div class="tlDot" :class="{ on: selectedVersionObj.status === 'signed_final' }"></div>
                <div class="tlBody">
                  <div class="tlTitle">Signed final</div>
                  <div class="tlSub mono">{{ selectedVersionObj.signedAt ? prettyDT(selectedVersionObj.signedAt) : "—" }}</div>
                </div>
              </div>
            </div>

            <!-- Revision Note -->
            <div class="section">
              <div class="sectionHead">
                <div>
                  <div class="sectionTitle">Revision note</div>
                  <div class="sectionSub">Internal only. Use this to record what changed in this version.</div>
                </div>
              </div>

              <div class="inlineAction">
                <input
                  class="inp inp--full"
                  v-model="editRevisionNotes[selectedVersionObj.id]"
                  :disabled="loading"
                  placeholder="Write a note for this version…"
                />
                <button class="btn btn--ghost" @click="saveRevisionNote(selectedVersionObj)" :disabled="loading">
                  Save Note
                </button>
              </div>

              <div v-if="selectedVersionObj.revisionNote" class="helperLine">
                Current note: {{ selectedVersionObj.revisionNote }}
              </div>
            </div>

            <!-- Generate Draft -->
            <div
              v-if="selectedVersionObj && !selectedVersionObj.hasQuotePdf && selectedVersionObj.status !== 'signed_final'"
              class="section sectionCard"
            >
              <div class="sectionHead">
                <div>
                  <div class="sectionTitle">Tentative Draft Builder</div>
                  <div class="sectionSub">
                    Use this when you want to generate an in-app quotation draft before the final customer-approved upload.
                  </div>
                </div>

                <div class="sectionActions sectionActions--top">
                  <button class="btn btn--ghost" @click="toggleTentativePanel" :disabled="loading">
                    {{ showTentativePanel ? "Hide Builder" : "Open Draft Builder" }}
                  </button>
                </div>
              </div>

              <div v-if="!showTentativePanel" class="collapsedHint">
                Hidden by default to keep the workspace clean.
              </div>

              <template v-else>
                <div class="infoStrip">
                  <div class="infoTitle">Tentative quotation</div>
                  <div class="infoText">
                    This generates the quotation PDF directly into the selected version. Standard notes are added automatically.
                  </div>
                </div>

                <div class="formSection">
                  <div class="formSectionTitle">Client Details</div>
                  <div class="cleanGrid cleanGrid--two">
                    <div class="field">
                      <label class="label">Client Name <span class="req">*</span></label>
                      <input class="inp" v-model="tentativeForm.clientName" placeholder="Client Name" />
                    </div>

                    <div class="field">
                      <label class="label">Quotation No <span class="req">*</span></label>
                      <input class="inp" v-model="tentativeForm.quotationNo" placeholder="Quotation No" />
                    </div>

                    <div class="field">
                      <label class="label">Address</label>
                      <input class="inp" v-model="tentativeForm.address" placeholder="Address" />
                    </div>

                    <div class="field">
                      <label class="label">Date <span class="req">*</span></label>
                      <input class="inp" v-model="tentativeForm.date" type="date" />
                    </div>

                    <div class="field">
                      <label class="label">Contact No</label>
                      <input class="inp" v-model="tentativeForm.contactNo" placeholder="Contact No" />
                    </div>

                    <div class="field">
                      <label class="label">Site Location</label>
                      <input class="inp" v-model="tentativeForm.siteLocation" placeholder="Site Location" />
                    </div>
                  </div>
                </div>

                <div class="formSection">
                  <div class="formSectionTitle">Kitchen Specification</div>

                  <div class="multiSectionGrid">
                    <div class="multiCard">
                      <div class="multiCardTitle">A. Base Units</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.baseUnits" :key="'base-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.baseUnits" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">B. Upper Units</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.upperUnits" :key="'upper-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.upperUnits" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">C. Tall Units</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.tallUnits" :key="'tall-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.tallUnits" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">D. Accessories</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.accessories" :key="'acc-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.accessories" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">E. Hardware</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.hardware" :key="'hard-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.hardware" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">F. Shutter Finishing</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.shutterFinishing" :key="'finish-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.shutterFinishing" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">G. Glass</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.glass" :key="'glass-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.glass" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="multiCard">
                      <div class="multiCardTitle">H. Handle</div>
                      <div class="checkGrid">
                        <label class="checkItem" v-for="x in tentativeOptions.handle" :key="'handle-' + x">
                          <input type="checkbox" :value="x" v-model="tentativeForm.handle" />
                          <span>{{ x }}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="formSection">
                  <div class="cleanGrid cleanGrid--two">
                    <div class="field">
                      <label class="label">Total Amount <span class="req">*</span></label>
                      <input class="inp" v-model="tentativeForm.totalAmount" placeholder="Total Amount" />
                    </div>

                    <div class="field">
                      <label class="label">Quick Summary</label>
                      <div class="summaryBox">
                        <div><b>Base:</b> {{ arrayDisplay(tentativeForm.baseUnits) }}</div>
                        <div><b>Upper:</b> {{ arrayDisplay(tentativeForm.upperUnits) }}</div>
                        <div><b>Tall:</b> {{ arrayDisplay(tentativeForm.tallUnits) }}</div>
                        <div><b>Accessories:</b> {{ arrayDisplay(tentativeForm.accessories) }}</div>
                        <div><b>Hardware:</b> {{ arrayDisplay(tentativeForm.hardware) }}</div>
                        <div><b>Finish:</b> {{ arrayDisplay(tentativeForm.shutterFinishing) }}</div>
                        <div><b>Glass:</b> {{ arrayDisplay(tentativeForm.glass) }}</div>
                        <div><b>Handle:</b> {{ arrayDisplay(tentativeForm.handle) }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="helperLine" v-if="tentativeValidationError">
                  {{ tentativeValidationError }}
                </div>
                <div class="helperLine helperLine--good" v-else>
                  Ready to generate a tentative quotation into this version.
                </div>

                <div class="sectionActions">
                  <button class="btn btn--ghost" @click="resetTentativeFormAndDefaults" :disabled="loading">
                    Reset Form
                  </button>
                  <button
                    class="btn"
                    @click="generateTentativeQuote(selectedVersionObj)"
                    :disabled="loading || !!tentativeValidationError"
                  >
                    {{ loading ? "Generating…" : "Generate Draft PDF" }}
                  </button>
                </div>
              </template>
            </div>

            <!-- Quote + Signed -->
            <div class="grid2">
              <div class="section sectionCard">
                <div class="sectionHead">
                  <div>
                    <div class="sectionTitle">Quotation PDF</div>
                    <div class="sectionSub">Upload the quotation received from Infurnia, or use the draft builder above.</div>
                  </div>
                </div>

                <div class="stack">
                  <div class="statusLine">
                    <span class="miniTag" :class="selectedVersionObj.hasQuotePdf ? 'miniTag--ok' : 'miniTag--muted'">
                      Quote PDF: {{ selectedVersionObj.hasQuotePdf ? "Available" : "Not uploaded" }}
                    </span>
                    <span
                      v-if="selectedVersionObj.hasQuotePdf"
                      class="miniTag"
                      :class="selectedVersionObj.isTentativeGenerated ? 'miniTag--warn' : 'miniTag--info'"
                    >
                      {{ selectedVersionObj.isTentativeGenerated ? "Generated in App" : "Uploaded from Infurnia" }}
                    </span>
                  </div>

                  <UploadBox
                    label="Quotation PDF"
                    helper="Upload QUOTE_PDF (one-time per version)"
                    accept="application/pdf"
                    :disabled="loading || selectedVersionObj.hasQuotePdf || selectedVersionObj.status === 'signed_final'"
                    disabledHint="Upload disabled for signed-final or already uploaded versions."
                    @picked="onPickedQuote($event, selectedVersionObj)"
                    @error="setBanner($event, 'error')"
                    @cleared="pickedQuoteFile=null; pickedQuoteFileId=null"
                  />

                  <button
                    class="btn"
                    @click="uploadQuote(selectedVersionObj)"
                    :disabled="loading || selectedVersionObj.hasQuotePdf || selectedVersionObj.status === 'signed_final' || !pickedQuoteFile || pickedQuoteFileId !== selectedVersionObj.id"
                  >
                    Upload Quote PDF
                  </button>

                  <div class="helperLine" v-if="quoteUploadDisabledReason">
                    {{ quoteUploadDisabledReason }}
                  </div>
                </div>
              </div>

              <div ref="signedSection" class="section sectionCard sectionCard--signed">
                <div class="sectionHead">
                  <div>
                    <div class="sectionTitle">Signed Final</div>
                    <div class="sectionSub">Upload the approved signed quotation after it has been sent to the client.</div>
                  </div>
                </div>

                <div class="stack">
                  <div class="statusLine">
                    <span class="miniTag" :class="selectedVersionObj.hasSignedFinal ? 'miniTag--ok' : 'miniTag--muted'">
                      Signed Final: {{ selectedVersionObj.hasSignedFinal ? "Available" : "Not uploaded" }}
                    </span>
                    <span class="miniTag miniTag--muted">Status: {{ statusLabel(selectedVersionObj) }}</span>
                  </div>

                  <UploadBox
                    label="Signed Final PDF"
                    helper="Upload SIGNED_FINAL"
                    accept="application/pdf"
                    :disabled="loading || selectedVersionObj.hasSignedFinal || selectedVersionObj.status === 'signed_final' || selectedVersionObj.status !== 'sent'"
                    disabledHint="You can upload signed final only after the quotation has been marked as sent."
                    @picked="onPickedSigned($event, selectedVersionObj)"
                    @error="setBanner($event, 'error')"
                    @cleared="pickedSignedFile=null; pickedSignedFileId=null"
                  />

                  <button
                    class="btn"
                    @click="signFinal(selectedVersionObj)"
                    :disabled="loading || selectedVersionObj.hasSignedFinal || selectedVersionObj.status === 'signed_final' || selectedVersionObj.status !== 'sent' || !pickedSignedFile || pickedSignedFileId !== selectedVersionObj.id || !selectedVersionObj.hasQuotePdf"
                  >
                    Upload Signed Final
                  </button>

                  <div class="helperLine" v-if="signedUploadDisabledReason">
                    {{ signedUploadDisabledReason }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Send -->
            <div class="section sectionCard sectionCard--send">
              <div class="sendRow">
                <div>
                  <div class="sectionTitle">Sent to client confirmation</div>
                  <div class="sectionSub">
                    Use this once your team has externally shared the quotation with the customer by email, WhatsApp, or another channel.
                  </div>
                </div>

                <button
                  class="btn btn--ghost"
                  @click="markSent(selectedVersionObj)"
                  :disabled="loading || !canMarkSent"
                >
                  Confirm Sent to Client
                </button>
              </div>

              <div class="helperLine" v-if="!canMarkSent">
                {{ markSentDisabledReason }}
              </div>
              <div class="helperLine helperLine--good" v-else>
                Ready to mark this version as sent.
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="inquiryId" class="card emptyCard">
          <div class="emptyCardTitle">No quotation versions yet</div>
          <div class="sectionSub">Create the first version to start documenting the quotation trail for this inquiry.</div>
          <div class="emptyCardActions">
            <button class="btn" @click="createNewVersion" :disabled="loading || !canCreateVersion">
              Create First Version
            </button>
          </div>
          <div v-if="canCreateVersionReason" class="helperLine">{{ canCreateVersionReason }}</div>
        </div>
      </div>

      <!-- Right / Context -->
      <div class="workspaceSide">
        <div class="card sideCard">
          <div class="sectionKicker">Inquiry</div>
          <div class="sectionTitle sideTitle">
            {{ inquiry && inquiry.customerName ? inquiry.customerName : "No client selected" }}
          </div>

          <div v-if="inquiryId" class="sectionSub sideSub">
            Inquiry ID: <b>{{ inquiryId }}</b>
            <span v-if="inquiry && inquiry.leadId"> • Lead ID: <b>{{ inquiry.leadId }}</b></span>
            <span v-if="inquiry && inquiry.customerPhone"> • {{ inquiry.customerPhone }}</span>
            <span v-if="inquiry && inquiry.leadCode"> • {{ inquiry.leadCode }}</span>
          </div>
          <div v-else class="sectionSub sideSub">
            Select a client above to begin the quotation workflow.
          </div>

          <div v-if="inquiryId" class="sideMeta">
            <div class="sideMetaItem">
              <div class="sideMetaLabel">Inquiry Docs</div>
              <div class="sideMetaValue">
                <span class="pill" :class="docsStateClass">
                  <span class="dot"></span>
                  <span class="pillValue">{{ docsStateLabel }}</span>
                </span>
              </div>
            </div>

            <div v-if="docsStatus && docsStatus.docsDueAt" class="sideMetaItem">
              <div class="sideMetaLabel">Deadline</div>
              <div class="sideMetaText">{{ prettyDT(docsStatus.docsDueAt) }}</div>
            </div>

            <div v-if="docsStatus && docsStatus.docsCompletedAt" class="sideMetaItem">
              <div class="sideMetaLabel">Completed</div>
              <div class="sideMetaText">{{ prettyDT(docsStatus.docsCompletedAt) }}</div>
            </div>

            <div class="sideMetaItem">
              <div class="sideMetaLabel">Versions</div>
              <div class="sideMetaText">{{ versions.length }}</div>
            </div>

            <div class="sideMetaItem">
              <div class="sideMetaLabel">Latest</div>
              <div class="sideMetaText">{{ latestVersion ? ("v" + latestVersion.version) : "—" }}</div>
            </div>

            <div class="sideMetaItem">
              <div class="sideMetaLabel">Signed Final</div>
              <div class="sideMetaText">{{ signedFinalVersion ? ("v" + signedFinalVersion.version) : "None" }}</div>
            </div>
          </div>
        </div>

        <div
          ref="orderSection"
          class="card sideCard sideCard--order"
          :class="{ 'sideCard--highlight': showPrimaryOrder }"
        >
          <div class="sectionKicker">Final Step</div>
          <div class="sectionTitle sideTitle">Create order from signed quotation</div>
          <div class="sectionSub sideSub">
            Final pricing is locked here before the quotation moves into the order flow.
          </div>

          <div class="orderInputs orderInputs--side">
            <div class="field">
              <label class="label">Actual Amount</label>
              <input
                class="inp"
                type="number"
                placeholder="Enter actual amount"
                v-model="orderPricing.actualAmount"
                :disabled="loading"
              />
            </div>

            <div class="field">
              <label class="label">Discount %</label>
              <input
                class="inp"
                type="number"
                placeholder="0 to 25"
                v-model="orderPricing.discountPct"
                :disabled="loading"
              />
            </div>

            <div class="finalBox">
              <div class="finalLabel">Final Amount</div>
              <div class="finalValue mono">{{ computedFinalAmountDisplay }}</div>
            </div>
          </div>

          <button class="btn btn--primaryWide" @click="placeOrderFromLatest" :disabled="loading || !canPlaceOrder">
            {{ placeOrderLabel }}
          </button>

          <div class="helperLine" v-if="!canPlaceOrder">
            {{ canPlaceOrderReason }}
          </div>
          <div class="helperLine helperLine--good" v-else>
            Ready to place order from signed-final quotation.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UploadBox from '@/components/quotations/UploadBox.vue'

function blankTentativeForm () {
  return {
    clientName: "",
    address: "",
    contactNo: "",
    siteLocation: "",
    quotationNo: "",
    date: "",
    baseUnits: [],
    upperUnits: [],
    tallUnits: [],
    accessories: [],
    hardware: [],
    shutterFinishing: [],
    glass: [],
    handle: [],
    totalAmount: ""
  }
}

export default {
  name: "SalesQuotations",
  components: { UploadBox },
  data () {
    return {
      loading: false,
      downloading: false,
      error: "",

      bannerMessage: "",
      bannerType: "neutral",

      inquiryId: null,
      inquiry: null,

      versions: [],
      docsStatus: null,
      leadId: null,
      orderId: null,

      pickedQuoteFile: null,
      pickedQuoteFileId: null,

      pickedSignedFile: null,
      pickedSignedFileId: null,

      orderPricing: {
        actualAmount: "",
        discountPct: ""
      },

      loadingReady: false,
      readySearch: "",
      readyList: [],
      selectedInquiryId: "",
      _debTimer: null,
      editRevisionNotes: {},
      selectedVersionKey: "",

      showTentativePanel: false,

      tentativeForm: blankTentativeForm(),

      tentativeOptions: {
        baseUnits: ["PLY", "Metal - G.I.", "S.S.", "HDHMR"],
        upperUnits: ["PLY", "Metal - G.I.", "S.S.", "HDHMR"],
        tallUnits: ["PLY", "Metal - G.I.", "S.S.", "HDHMR"],
        accessories: ["Hettich", "Hafele/Blum", "Imported"],
        hardware: ["Hettich", "Hafele/Blum", "Imported"],
        shutterFinishing: ["Laminate", "Acrylic", "PU", "Venner / Wood"],
        glass: ["Plain", "Fluted", "Designer", "Tinted"],
        handle: ["Profile handle", "handles", "knobs"]
      }
    }
  },
  computed: {
    sortedVersionsDesc () {
      if (!this.versions || !this.versions.length) return []
      return this.versions.slice().sort(function (a, b) {
        return (b.version || 0) - (a.version || 0)
      })
    },

    latestVersion () {
      return this.sortedVersionsDesc[0] || null
    },

    selectedVersionObj () {
      if (!this.selectedVersionKey) return this.latestVersion
      var i
      for (i = 0; i < this.versions.length; i++) {
        if (String(this.versions[i].id) === String(this.selectedVersionKey)) return this.versions[i]
      }
      return this.latestVersion
    },

    selectedReady () {
      if (!this.selectedInquiryId) return null
      var i
      for (i = 0; i < this.readyList.length; i++) {
        if (String(this.readyList[i].inquiryId) === String(this.selectedInquiryId)) return this.readyList[i]
      }
      return null
    },

    docsStateLabel () {
      var st = this.docsStatus
      if (!st) return "—"
      if (st.docsState === "COMPLETE") return "Complete"
      if (st.docsState === "OVERDUE") return "Overdue"
      return "Pending"
    },

    docsStateClass () {
      var st = this.docsStatus
      if (!st) return "pill--neutral"
      if (st.docsState === "COMPLETE") return "pill--good"
      if (st.docsState === "OVERDUE") return "pill--bad"
      return "pill--warn"
    },

    hasAnySignedFinalInList () {
      var i
      for (i = 0; i < this.versions.length; i++) {
        if ((this.versions[i].status || "") === "signed_final") return true
      }
      return false
    },

    canCreateVersion () {
      return !!(this.inquiryId && this.docsStatus && this.docsStatus.docsOk && !this.hasAnySignedFinalInList && !this.orderId)
    },

    canCreateVersionReason () {
      if (!this.inquiryId) return "Select an inquiry first."
      if (!this.docsStatus || !this.docsStatus.docsOk) return "Inquiry documents must be complete before creating a quotation version."
      if (this.hasAnySignedFinalInList) return "A signed final quotation already exists, so this quotation trail is locked."
      if (this.orderId) return "An order already exists for this inquiry."
      return ""
    },

    signedFinalVersion () {
      if (!this.versions || !this.versions.length) return null
      var signed = this.versions.filter(function (v) {
        return v.status === "signed_final" && v.hasSignedFinal
      })
      if (!signed.length) return null
      signed.sort(function (a, b) {
        return (b.version || 0) - (a.version || 0)
      })
      return signed[0]
    },

    computedFinalAmount () {
      var aa = parseFloat(this.orderPricing.actualAmount)
      var dp = parseFloat(this.orderPricing.discountPct)
      if (isNaN(aa) || aa <= 0) return null
      if (isNaN(dp)) dp = 0
      if (dp < 0) dp = 0
      if (dp > 25) dp = 25
      return Math.round(aa * (1 - (dp / 100)))
    },

    computedFinalAmountDisplay () {
      return this.computedFinalAmount === null ? "—" : String(this.computedFinalAmount)
    },

    canPlaceOrder () {
      if (!this.docsStatus || !this.docsStatus.docsOk) return false
      if (this.orderId) return false
      var q = this.signedFinalVersion
      return !!(q && q.hasSignedFinal)
    },

    canPlaceOrderReason () {
      if (!this.docsStatus || !this.docsStatus.docsOk) return "Inquiry documents must be complete."
      if (this.orderId) return "An order has already been created for this inquiry."
      if (!this.signedFinalVersion || !this.signedFinalVersion.hasSignedFinal) return "A signed-final quotation is required before placing an order."
      return ""
    },

    placeOrderLabel () {
      return this.orderId ? "Order Already Exists" : "Place Order (Signed Final)"
    },

    tentativeValidationError () {
      if (!this.tentativeForm.clientName) return "Client Name is required."
      if (!this.tentativeForm.quotationNo) return "Quotation No is required."
      if (!this.tentativeForm.date) return "Date is required."
      if (!this.tentativeForm.totalAmount) return "Total Amount is required."
      return ""
    },

    canMarkSent () {
      if (!this.selectedVersionObj) return false
      if (!this.selectedVersionObj.hasQuotePdf) return false
      if (this.selectedVersionObj.status === "signed_final") return false
      if (this.selectedVersionObj.status === "sent") return false
      return true
    },

    markSentDisabledReason () {
      if (!this.selectedVersionObj) return "Select a quotation version first."
      if (!this.selectedVersionObj.hasQuotePdf) return "Generate or upload the quotation PDF first."
      if (this.selectedVersionObj.status === "signed_final") return "This version is already signed final."
      if (this.selectedVersionObj.status === "sent") return "This version is already marked as sent."
      return ""
    },

    quoteUploadDisabledReason () {
      if (!this.selectedVersionObj) return ""
      if (this.selectedVersionObj.hasQuotePdf) return "A quotation PDF already exists for this version."
      if (this.selectedVersionObj.status === "signed_final") return "Signed-final versions cannot accept a new quote PDF."
      if (!this.pickedQuoteFile || this.pickedQuoteFileId !== this.selectedVersionObj.id) return "Choose a PDF file to enable upload."
      return ""
    },

    signedUploadDisabledReason () {
      if (!this.selectedVersionObj) return ""
      if (!this.selectedVersionObj.hasQuotePdf) return "Quotation PDF must exist before uploading signed final."
      if (this.selectedVersionObj.status !== "sent") return "Mark this quotation as sent to the client before uploading signed final."
      if (this.selectedVersionObj.hasSignedFinal) return "Signed final already exists for this version."
      if (this.selectedVersionObj.status === "signed_final") return "This version is already locked as signed final."
      if (!this.pickedSignedFile || this.pickedSignedFileId !== this.selectedVersionObj.id) return "Choose a signed-final PDF to enable upload."
      return ""
    },

    currentStageKey () {
      if (!this.inquiryId) return "select"
      if (!this.docsStatus || !this.docsStatus.docsOk) return "docs"
      if (!this.versions.length) return "version"
      if (!this.selectedVersionObj) return "version"
      if (!this.selectedVersionObj.hasQuotePdf) return "quote"
      if (this.selectedVersionObj.status !== "sent" && this.selectedVersionObj.status !== "signed_final") return "sent"
      if (!this.signedFinalVersion) return "sign"
      if (!this.orderId) return "order"
      return "done"
    },

    currentStageLabel () {
      var map = {
        select: "Select Inquiry",
        docs: "Docs Pending",
        version: "Version Setup",
        quote: "Quotation Needed",
        sent: "Send Pending",
        sign: "Signed Final Pending",
        order: "Ready for Order",
        done: "Completed"
      }
      return map[this.currentStageKey] || "Quotation Pipeline"
    },

    activeActionTitle () {
      if (!this.inquiryId) return "Select a client inquiry"
      if (!this.docsStatus || !this.docsStatus.docsOk) return "Inquiry documents are not complete yet"
      if (!this.versions.length) return "Create the first quotation version"
      if (!this.selectedVersionObj) return "Select a quotation version"
      if (!this.selectedVersionObj.hasQuotePdf) return "Add the quotation PDF for this version"
      if (this.selectedVersionObj.status !== "sent" && this.selectedVersionObj.status !== "signed_final") return "Confirm quotation sent to client"
      if (!this.signedFinalVersion) return "Upload the signed final quotation"
      if (!this.orderId) return "Complete order placement"
      return "Quotation pipeline completed"
    },

    activeActionSub () {
      if (!this.inquiryId) return "Choose a ready inquiry to begin quotation work."
      if (!this.docsStatus || !this.docsStatus.docsOk) return "This inquiry cannot move into quotation work until the required inquiry documents are complete."
      if (!this.versions.length) return "Start the quotation trail so every round of discussion with the client is recorded properly."
      if (!this.selectedVersionObj) return "Pick the version you want to work on."
      if (!this.selectedVersionObj.hasQuotePdf) return "Either generate a tentative draft here or upload the actual quotation received from Infurnia."
      if (this.selectedVersionObj.status !== "sent" && this.selectedVersionObj.status !== "signed_final") return "Once the quote has been externally shared, confirm it here so the team knows the client has received it."
      if (!this.signedFinalVersion) return "Only a sent quotation can become a signed final. Signed final locks the quotation trail."
      if (!this.orderId) return "Signed final exists. Enter final pricing and create the order."
      return "This inquiry already has a completed quotation-to-order trail."
    },

    activeActionReason () {
      if (!this.inquiryId) return ""
      if (!this.docsStatus || !this.docsStatus.docsOk) return this.canCreateVersionReason
      if (!this.versions.length) return this.canCreateVersionReason
      if (!this.selectedVersionObj) return "Select a version to continue."
      if (!this.selectedVersionObj.hasQuotePdf) return this.quoteUploadDisabledReason && this.quoteUploadDisabledReason !== "Choose a PDF file to enable upload." ? this.quoteUploadDisabledReason : ""
      if (this.selectedVersionObj.status !== "sent" && this.selectedVersionObj.status !== "signed_final") return this.markSentDisabledReason && this.markSentDisabledReason !== "This version is already marked as sent." ? this.markSentDisabledReason : ""
      if (!this.signedFinalVersion) return this.signedUploadDisabledReason && this.signedUploadDisabledReason !== "Choose a signed-final PDF to enable upload." ? this.signedUploadDisabledReason : ""
      if (!this.orderId) return this.canPlaceOrderReason
      return "Order already exists for this inquiry."
    },

    activeActionGoodLine () {
      if (!this.inquiryId) return ""
      if (this.currentStageKey === "version") return "Ready to create the first quotation version."
      if (this.currentStageKey === "quote") return "Ready to upload or generate the quotation."
      if (this.currentStageKey === "sent") return "Ready to confirm the quotation was sent to the client."
      if (this.currentStageKey === "sign") return "Ready to upload the signed-final quotation."
      if (this.currentStageKey === "order") return "Ready to place the order from signed final."
      if (this.currentStageKey === "done") return "This quotation pipeline is already completed."
      return ""
    },

    showPrimaryCreateVersion () {
      return this.currentStageKey === "version"
    },

    showPrimaryGenerate () {
      return this.currentStageKey === "quote"
    },

    showPrimaryMarkSent () {
      return this.currentStageKey === "sent"
    },

    showPrimarySign () {
      return this.currentStageKey === "sign"
    },

    showPrimaryOrder () {
      return this.currentStageKey === "order"
    },

    showSecondaryCreateVersion () {
      return this.versions.length > 0 && this.canCreateVersion
    },

    pipelineSteps () {
      return [
        {
          key: "docs",
          label: "Docs",
          hint: this.docsStatus && this.docsStatus.docsOk ? "Complete" : "Pending",
          state: this.pipelineStateFor("docs")
        },
        {
          key: "version",
          label: "Version",
          hint: this.versions.length ? (this.versions.length + " created") : "Create first",
          state: this.pipelineStateFor("version")
        },
        {
          key: "quote",
          label: "Quote Ready",
          hint: this.selectedVersionObj && this.selectedVersionObj.hasQuotePdf ? "Attached" : "Pending",
          state: this.pipelineStateFor("quote")
        },
        {
          key: "sent",
          label: "Sent",
          hint: this.selectedVersionObj && (this.selectedVersionObj.status === "sent" || this.selectedVersionObj.status === "signed_final") ? "Confirmed" : "Pending",
          state: this.pipelineStateFor("sent")
        },
        {
          key: "sign",
          label: "Signed Final",
          hint: this.signedFinalVersion ? ("v" + this.signedFinalVersion.version) : "Pending",
          state: this.pipelineStateFor("sign")
        },
        {
          key: "order",
          label: "Order",
          hint: this.orderId ? "Created" : "Pending",
          state: this.pipelineStateFor("order")
        }
      ]
    }
  },

  created () {
    var q = (this.$route && this.$route.query) ? this.$route.query : {}

    var iid = q.inquiryId || q.iid
    this.inquiryId = iid ? String(iid) : null
    this.selectedInquiryId = this.inquiryId || ""

    this.leadId = q.leadId ? parseInt(q.leadId) : null
    this.orderId = q.orderId ? String(q.orderId) : (q.oid ? String(q.oid) : null)

    this.fetchReady().then(() => {
      if (this.inquiryId) this.refreshAll()
    })
  },

  methods: {
    pipelineStateFor (key) {
      var order = ["docs", "version", "quote", "sent", "sign", "order"]
      var current = this.currentStageKey
      var currentIndex = order.indexOf(current)
      var stepIndex = order.indexOf(key)

      if (current === "select") return "locked"
      if (current === "done") return "done"

      if (currentIndex === -1 || stepIndex === -1) return "locked"
      if (stepIndex < currentIndex) return "done"
      if (stepIndex === currentIndex) return "current"
      return "locked"
    },

    pipeStepClass (step) {
      return {
        "pipeStep--done": step.state === "done",
        "pipeStep--current": step.state === "current",
        "pipeStep--locked": step.state === "locked"
      }
    },

    versionPillClass (v) {
      return {
        "versionPill--active": this.selectedVersionObj && String(this.selectedVersionObj.id) === String(v.id),
        "versionPill--signed": v.status === "signed_final",
        "versionPill--sent": v.status === "sent"
      }
    },

    selectVersion (v) {
      this.selectedVersionKey = String(v.id)
      this.onSelectVersion()
    },

    toggleTentativePanelForPrimary () {
      this.toggleTentativePanel()
      if (this.showTentativePanel) {
        var self = this
        this.$nextTick(function () {
          var section = self.$el.querySelector('.sectionCard')
          if (section && section.scrollIntoView) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },

    focusSignedSection () {
      var self = this
      this.$nextTick(function () {
        var el = self.$refs.signedSection
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    focusOrderSection () {
      var self = this
      this.$nextTick(function () {
        var el = self.$refs.orderSection
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    setBanner (msg, type) {
      this.bannerMessage = msg || ""
      this.bannerType = type || "neutral"
    },

    clearBanner () {
      this.bannerMessage = ""
      this.bannerType = "neutral"
    },

    showError (msg) {
      this.error = msg || ""
      this.setBanner(this.error, "error")
    },

    showSuccess (msg) {
      this.setBanner(msg || "", "success")
    },

    prettyDT (s) {
      if (!s) return "—"
      try {
        var d = new Date(s)
        if (isNaN(d.getTime())) return String(s)
        return d.toLocaleString()
      } catch (e) {
        return String(s)
      }
    },

    prettyDTShort (s) {
      if (!s) return "—"
      try {
        var d = new Date(s)
        if (isNaN(d.getTime())) return String(s)
        return d.toLocaleDateString()
      } catch (e) {
        return String(s)
      }
    },

    arrayDisplay (arr) {
      if (!arr || !arr.length) return "—"
      return arr.join(", ")
    },

    joinSelections (arr) {
      if (!arr || !arr.length) return ""
      return arr.join(", ")
    },

    toggleTentativePanel () {
      this.showTentativePanel = !this.showTentativePanel
      if (this.showTentativePanel && this.selectedVersionObj) {
        this.fillTentativeDefaults(this.selectedVersionObj)
      }
    },

    debouncedFetchReady () {
      clearTimeout(this._debTimer)
      var self = this
      this._debTimer = setTimeout(function () {
        self.fetchReady()
      }, 250)
    },

    async fetchReady () {
      this.loadingReady = true
      try {
        const res = await this.$api.get("/quotations/ready", {
          params: { q: (this.readySearch || "").trim(), limit: 200 }
        })
        this.readyList = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        this.showError(this.pickErr(e, "Failed to load ready inquiries"))
      } finally {
        this.loadingReady = false
      }
    },

    fillTentativeDefaults (q) {
      var today = new Date()
      var yyyy = today.getFullYear()
      var mm = String(today.getMonth() + 1).padStart(2, "0")
      var dd = String(today.getDate()).padStart(2, "0")

      this.tentativeForm.clientName = this.tentativeForm.clientName || ((this.inquiry && this.inquiry.customerName) ? this.inquiry.customerName : "")
      this.tentativeForm.contactNo = this.tentativeForm.contactNo || ((this.inquiry && this.inquiry.customerPhone) ? this.inquiry.customerPhone : "")
      this.tentativeForm.address = this.tentativeForm.address || ""
      this.tentativeForm.siteLocation = this.tentativeForm.siteLocation || ""
      this.tentativeForm.date = this.tentativeForm.date || (yyyy + "-" + mm + "-" + dd)
      this.tentativeForm.quotationNo = this.tentativeForm.quotationNo || ("EDDA-Q-" + q.id)
    },

    resetTentativeForm () {
      this.tentativeForm = blankTentativeForm()
    },

    resetTentativeFormAndDefaults () {
      this.resetTentativeForm()
      if (this.selectedVersionObj) this.fillTentativeDefaults(this.selectedVersionObj)
    },

    async generateTentativeQuote (q) {
      if (this.tentativeValidationError) {
        this.showError(this.tentativeValidationError)
        return
      }

      this.error = ""
      this.clearBanner()
      this.loading = true

      try {
        await this.$api.post("/quotations/" + q.id + "/generate_tentative_pdf", {
          clientName: this.tentativeForm.clientName,
          address: this.tentativeForm.address,
          contactNo: this.tentativeForm.contactNo,
          siteLocation: this.tentativeForm.siteLocation,
          quotationNo: this.tentativeForm.quotationNo,
          date: this.tentativeForm.date,
          baseUnits: this.joinSelections(this.tentativeForm.baseUnits),
          upperUnits: this.joinSelections(this.tentativeForm.upperUnits),
          tallUnits: this.joinSelections(this.tentativeForm.tallUnits),
          accessories: this.joinSelections(this.tentativeForm.accessories),
          hardware: this.joinSelections(this.tentativeForm.hardware),
          shutterFinishing: this.joinSelections(this.tentativeForm.shutterFinishing),
          glass: this.joinSelections(this.tentativeForm.glass),
          handle: this.joinSelections(this.tentativeForm.handle),
          notes: [],
          totalAmount: this.tentativeForm.totalAmount
        })

        await this.fetchQuotations()
        this.showTentativePanel = false
        this.showSuccess("Tentative quotation PDF generated and attached to this version successfully.")
      } catch (e) {
        this.showError(this.pickErr(e, "Failed to generate tentative quotation"))
      } finally {
        this.loading = false
      }
    },

    onSelectInquiry () {
      var iid = this.selectedInquiryId ? String(this.selectedInquiryId) : ""

      this.inquiryId = iid || null
      this.inquiry = null
      this.docsStatus = null
      this.versions = []
      this.error = ""
      this.clearBanner()
      this.editRevisionNotes = {}
      this.resetTentativeForm()

      this.pickedQuoteFile = null
      this.pickedQuoteFileId = null
      this.pickedSignedFile = null
      this.pickedSignedFileId = null
      this.selectedVersionKey = ""
      this.showTentativePanel = false
      this.orderPricing.actualAmount = ""
      this.orderPricing.discountPct = ""

      if (!iid) {
        this.$router.replace({ name: "sales-quotations", query: {} })
        return
      }

      this.$router.replace({ name: "sales-quotations", query: { inquiryId: iid } })
      this.refreshAll()
    },

    onSelectVersion () {
      this.pickedQuoteFile = null
      this.pickedQuoteFileId = null
      this.pickedSignedFile = null
      this.pickedSignedFileId = null
      this.clearBanner()
      this.resetTentativeForm()
      this.showTentativePanel = false

      if (this.selectedVersionObj) {
        this.fillTentativeDefaults(this.selectedVersionObj)
      }
    },

    badgeClass (q) {
      var st = (q.status || "").toLowerCase()
      if (st === "signed_final") return "badge--good"
      if (st === "sent") return "badge--info"
      return "badge--neutral"
    },

    statusLabel (q) {
      var st = (q.status || "").toLowerCase()
      if (st === "signed_final") return "Signed Final"
      if (st === "sent") return "Sent"
      if (st === "revised") return "Revised"
      if (st === "draft") return "Draft"
      return (q.status || "—")
    },

    onPickedQuote (payload, q) {
      this.pickedQuoteFile = payload
      this.pickedQuoteFileId = q.id
      this.pickedSignedFile = null
      this.pickedSignedFileId = null
      this.showSuccess("Quotation PDF selected. Click Upload Quote PDF to save it.")
    },

    onPickedSigned (payload, q) {
      this.pickedSignedFile = payload
      this.pickedSignedFileId = q.id
      this.pickedQuoteFile = null
      this.pickedQuoteFileId = null
      this.showSuccess("Signed-final PDF selected. Click Upload Signed Final to save it.")
    },

    async refreshAll () {
      if (!this.inquiryId) return
      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        await Promise.all([this.fetchInquiryAndDocs(), this.fetchQuotations()])
      } catch (e) {
        this.showError(this.pickErr(e, "Failed to refresh"))
      } finally {
        this.loading = false
      }
    },

    async fetchInquiryAndDocs () {
      var iid = this.inquiryId
      if (!iid) return
      var res = await this.$api.get("/inquiries/" + iid)
      var data = res.data || {}
      this.inquiry = data.inquiry || null
      this.docsStatus = data.docs || null
      this.leadId = this.inquiry && this.inquiry.leadId ? parseInt(this.inquiry.leadId) : this.leadId
    },

    async fetchQuotations () {
      var iid = this.inquiryId
      if (!iid) return

      var res = await this.$api.get("/quotations", { params: { inquiryId: iid } })
      this.versions = Array.isArray(res.data) ? res.data : []

      var i
      for (i = 0; i < this.versions.length; i++) {
        var qq = this.versions[i]
        if (this.editRevisionNotes[qq.id] === undefined) {
          this.$set(this.editRevisionNotes, qq.id, (qq.revisionNote || ""))
        }
      }

      if (this.versions.length) {
        var keep = false
        var j
        for (j = 0; j < this.versions.length; j++) {
          if (String(this.versions[j].id) === String(this.selectedVersionKey)) {
            keep = true
            break
          }
        }
        if (!keep) {
          this.selectedVersionKey = this.latestVersion ? String(this.latestVersion.id) : ""
        }
      } else {
        this.selectedVersionKey = ""
      }

      if (this.selectedVersionObj) {
        this.resetTentativeForm()
        this.fillTentativeDefaults(this.selectedVersionObj)
      }
    },

    async saveRevisionNote (q) {
      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        const note = (this.editRevisionNotes[q.id] || "").trim()
        await this.$api.patch("/quotations/" + q.id, { revisionNote: note || null })
        await this.fetchQuotations()
        this.showSuccess("Revision note saved.")
      } catch (e) {
        this.showError(this.pickErr(e, "Failed to save note"))
      } finally {
        this.loading = false
      }
    },

    goToInquiryAttachments () {
      if (!this.leadId) return
      this.$router.push({ name: "sales-customer-detail", params: { id: this.leadId } })
    },

    async createNewVersion () {
      if (!this.inquiryId) {
        this.showError("Select an inquiry first.")
        return
      }
      if (!this.docsStatus || !this.docsStatus.docsOk) {
        this.showError("Inquiry documents are not complete yet. Upload docs first.")
        return
      }
      if (this.hasAnySignedFinalInList) {
        this.showError("This inquiry is locked because a signed final quotation already exists.")
        return
      }
      if (this.orderId) {
        this.showError("An order already exists for this inquiry.")
        return
      }

      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        await this.$api.post("/quotations", {
          inquiryId: this.inquiryId,
          revisionNote: null
        })
        await this.fetchQuotations()
        this.showSuccess("New quotation version created.")
      } catch (e) {
        this.showError(this.pickErr(e, "Failed to create version"))
      } finally {
        this.loading = false
      }
    },

    async uploadQuote (q) {
      if (!this.pickedQuoteFile || this.pickedQuoteFileId !== q.id) {
        this.showError("Choose a quotation PDF first.")
        return
      }

      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        await this.$api.post("/quotations/" + q.id + "/upload_quote", {
          dataUrl: this.pickedQuoteFile.dataUrl,
          filename: this.pickedQuoteFile.filename || "quotation.pdf"
        })

        this.pickedQuoteFile = null
        this.pickedQuoteFileId = null
        await this.fetchQuotations()
        this.showSuccess("Quotation PDF uploaded successfully.")
      } catch (e) {
        this.pickedQuoteFile = null
        this.pickedQuoteFileId = null
        this.showError(this.pickErr(e, "Failed to upload quote"))
      } finally {
        this.loading = false
      }
    },

    async markSent (q) {
      if (!this.canMarkSent) {
        this.showError(this.markSentDisabledReason || "This version cannot be marked as sent right now.")
        return
      }

      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        await this.$api.post("/quotations/" + q.id + "/send")
        await this.fetchQuotations()
        this.showSuccess("Quotation marked as sent.")
      } catch (e) {
        this.showError(this.pickErr(e, "Failed to mark sent"))
      } finally {
        this.loading = false
      }
    },

    async signFinal (q) {
      if (!this.pickedSignedFile || this.pickedSignedFileId !== q.id) {
        this.showError("Choose a signed-final PDF first.")
        return
      }
      if (!q.hasQuotePdf) {
        this.showError("Upload or generate the quotation PDF first.")
        return
      }
      if (q.status !== "sent") {
        this.showError("Mark the quotation as sent before uploading signed final.")
        return
      }

      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        await this.$api.post("/quotations/" + q.id + "/sign_final", {
          dataUrl: this.pickedSignedFile.dataUrl,
          filename: this.pickedSignedFile.filename || "signed_final.pdf"
        })

        this.pickedSignedFile = null
        this.pickedSignedFileId = null
        await this.fetchQuotations()
        this.showSuccess("Signed-final PDF uploaded successfully.")
      } catch (e) {
        this.pickedSignedFile = null
        this.pickedSignedFileId = null
        this.showError(this.pickErr(e, "Failed to upload signed final"))
      } finally {
        this.loading = false
      }
    },

    async placeOrderFromLatest () {
      var q = this.signedFinalVersion
      if (!q) {
        this.showError("A signed-final quotation is required before placing an order.")
        return
      }
      if (!this.canPlaceOrder) {
        this.showError(this.canPlaceOrderReason || "Order cannot be placed yet.")
        return
      }

      this.error = ""
      this.clearBanner()
      this.loading = true
      try {
        var aa = parseFloat(this.orderPricing.actualAmount)
        var dp = parseFloat(this.orderPricing.discountPct)
        var fa = this.computedFinalAmount

        if (isNaN(aa) || aa <= 0) {
          this.showError("Actual Amount is required.")
          this.loading = false
          return
        }
        if (isNaN(dp)) dp = 0
        if (dp < 0 || dp > 25) {
          this.showError("Discount % must be between 0 and 25.")
          this.loading = false
          return
        }
        if (!fa || fa <= 0) {
          this.showError("Final Amount looks invalid.")
          this.loading = false
          return
        }

        var payload = {
          quotationId: q.id,
          actualAmount: Math.round(aa),
          discountPct: Math.round(dp * 100) / 100,
          finalAmount: fa
        }

        var oid = null

        if (this.orderId) {
          this.showError("An order already exists for this inquiry.")
          this.loading = false
          return
        } else {
          var res = await this.$api.post("/orders", payload)
          oid = res && res.data ? res.data.id : null
        }

        if (oid) {
          this.$router.push({ name: "sales-order-detail", params: { id: oid } })
        } else {
          await this.refreshAll()
          this.showSuccess("Order placed successfully.")
        }
      } catch (e) {
        var d = e && e.response && e.response.data ? e.response.data : null
        if (d && d.error === "already_ordered" && d.orderId) {
          this.$router.push({ name: "sales-order-detail", params: { id: d.orderId } })
          return
        }
        this.showError(this.pickErr(e, "Failed to place order"))
      } finally {
        this.loading = false
      }
    },

    async downloadQuote (q) {
      if (!q || !q.id || !q.hasQuotePdf) {
        this.showError("Quotation PDF is not available for this version.")
        return
      }
      var fid = await this.getFileIdByKind(q.id, "QUOTE_PDF")
      if (!fid) {
        this.showError("Quote PDF not found on server for this version.")
        return
      }
      await this.downloadAuthed("/quotations/files/" + fid + "/download", "quote_v" + q.version + "_" + q.id + ".pdf")
    },

    async downloadSigned (q) {
      if (!q || !q.id || !q.hasSignedFinal) {
        this.showError("Signed final PDF is not available for this version.")
        return
      }
      var fid = await this.getFileIdByKind(q.id, "SIGNED_FINAL")
      if (!fid) {
        this.showError("Signed final PDF not found on server for this version.")
        return
      }
      await this.downloadAuthed("/quotations/files/" + fid + "/download", "signed_final_v" + q.version + "_" + q.id + ".pdf")
    },

    async getFileIdByKind (quotationId, kind) {
      try {
        var res = await this.$api.get("/quotations/" + quotationId + "/files")
        var arr = Array.isArray(res.data) ? res.data : []
        var i
        for (i = 0; i < arr.length; i++) {
          if ((arr[i].fileKind || "") === kind) return arr[i].id
        }
      } catch (e) {}
      return null
    },

    async downloadAuthed (path, filename) {
      this.downloading = true
      this.clearBanner()
      try {
        var res = await this.$api.get(path, { responseType: "blob" })
        var blob = res.data
        var url = window.URL.createObjectURL(blob)

        var a = document.createElement("a")
        a.href = url
        a.download = filename || "file"
        document.body.appendChild(a)
        a.click()
        a.remove()

        window.URL.revokeObjectURL(url)
        this.showSuccess("Download started.")
      } catch (e) {
        this.showError(this.pickErr(e, "Download failed"))
      } finally {
        this.downloading = false
      }
    },

    pickErr (e, fallback) {
      try {
        if (e && e.response && e.response.data) {
          var d = e.response.data
          if (d.error) return d.error
          if (d.errors && d.errors.length) return d.errors.join(", ")
        }
        if (e && e.message) return e.message
      } catch (x) {}
      return fallback || "failed"
    }
  }
}
</script>

<style scoped>
.page{
  padding:24px;
  background:
    radial-gradient(circle at top left, rgba(59,130,246,.06), transparent 28%),
    linear-gradient(180deg, #f8fafc 0%, #f4f7fb 100%);
  min-height:100%;
  color:#0f172a;
}

.card{
  background:rgba(255,255,255,.92);
  backdrop-filter:blur(8px);
  border:1px solid rgba(15,23,42,.08);
  border-radius:24px;
  padding:20px;
  box-shadow:0 20px 50px rgba(15,23,42,.06);
}

.card + .card{
  margin-top:14px;
}

.sectionKicker{
  font-size:12px;
  font-weight:800;
  color:#64748b;
  text-transform:uppercase;
  letter-spacing:.06em;
}

.sectionTitle{
  margin-top:4px;
  font-size:22px;
  font-weight:800;
  letter-spacing:-.02em;
}

.sectionSub{
  margin-top:6px;
  color:#64748b;
  font-size:13px;
  line-height:1.55;
}

.cardHead{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:16px;
  flex-wrap:wrap;
  margin-bottom:16px;
}

.cardHead--tight{
  margin-bottom:14px;
}

/* Pipeline */
.pipelineShell{
  margin-bottom:16px;
  padding:22px;
}

.pipelineTop{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:18px;
  flex-wrap:wrap;
}

.pipelineTitle{
  margin-top:4px;
  font-size:32px;
  line-height:1.05;
  font-weight:800;
  letter-spacing:-.03em;
}

.pipelineStats{
  display:grid;
  grid-template-columns:repeat(3, minmax(120px, 1fr));
  gap:10px;
  min-width:360px;
}

.statChip{
  background:rgba(255,255,255,.85);
  border:1px solid rgba(15,23,42,.08);
  border-radius:18px;
  padding:14px;
  box-shadow:0 14px 28px rgba(15,23,42,.05);
}

.statChipLabel{
  font-size:12px;
  font-weight:700;
  color:#64748b;
}

.statChipValue{
  margin-top:8px;
  font-size:19px;
  font-weight:800;
  letter-spacing:-.02em;
}

.pipelineRow{
  margin-top:18px;
  display:grid;
  grid-template-columns:repeat(6, minmax(0, 1fr));
  gap:12px;
}

.pipeStep{
  position:relative;
  display:flex;
  align-items:flex-start;
  gap:12px;
  padding:14px;
  border-radius:20px;
  border:1px solid rgba(15,23,42,.08);
  background:#fbfdff;
  min-height:86px;
}

.pipeIndex{
  width:28px;
  height:28px;
  border-radius:999px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:12px;
  font-weight:800;
  background:rgba(148,163,184,.18);
  color:#334155;
  border:1px solid rgba(148,163,184,.22);
  flex:0 0 auto;
}

.pipeBody{
  min-width:0;
}

.pipeLabel{
  font-size:13px;
  font-weight:800;
  color:#0f172a;
}

.pipeHint{
  margin-top:4px;
  font-size:12px;
  line-height:1.4;
  color:#64748b;
}

.pipeStep--done{
  background:rgba(16,185,129,.08);
  border-color:rgba(16,185,129,.18);
}
.pipeStep--done .pipeIndex{
  background:#10b981;
  color:#fff;
  border-color:#10b981;
}

.pipeStep--current{
  background:rgba(59,130,246,.08);
  border-color:rgba(59,130,246,.22);
  box-shadow:0 0 0 1px rgba(59,130,246,.08), 0 16px 32px rgba(59,130,246,.08);
}
.pipeStep--current .pipeIndex{
  background:#1d4ed8;
  color:#fff;
  border-color:#1d4ed8;
}

.pipeStep--locked{
  opacity:.76;
}

.pipeConnector{
  position:absolute;
  top:50%;
  right:-12px;
  width:12px;
  height:1px;
  background:rgba(148,163,184,.45);
}

/* Banner */
.banner{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  border-radius:18px;
  padding:14px 16px;
  margin-bottom:16px;
  border:1px solid rgba(15,23,42,.08);
  box-shadow:0 12px 30px rgba(15,23,42,.04);
}
.banner--error{
  background:rgba(239,68,68,.08);
  border-color:rgba(239,68,68,.18);
  color:#991b1b;
}
.banner--success{
  background:rgba(16,185,129,.10);
  border-color:rgba(16,185,129,.18);
  color:#065f46;
}
.banner--neutral{
  background:#fff;
}
.bannerText{
  font-weight:700;
  line-height:1.5;
}
.bannerClose{
  border:none;
  background:transparent;
  cursor:pointer;
  font-size:22px;
  line-height:1;
  color:inherit;
}

/* Inputs + buttons */
.pickerGrid,
.cleanGrid{
  display:grid;
  gap:14px;
}
.pickerGrid{
  grid-template-columns:minmax(260px,360px) 1fr;
}
.cleanGrid--two{
  grid-template-columns:1fr 1fr;
}
.field{
  display:flex;
  flex-direction:column;
  gap:8px;
  min-width:0;
}
.label{
  font-size:12px;
  font-weight:800;
  color:#334155;
}
.req{
  color:#dc2626;
}
.inp{
  width:100%;
  min-width:0;
  border:1px solid rgba(15,23,42,.10);
  border-radius:16px;
  padding:13px 14px;
  outline:none;
  background:#fff;
  color:#0f172a;
  transition:border-color .18s ease, box-shadow .18s ease;
  font-size:14px;
}
.inp:focus{
  border-color:rgba(59,130,246,.55);
  box-shadow:0 0 0 4px rgba(59,130,246,.10);
}
.inp--full{
  flex:1;
}

.btn{
  border:1px solid rgba(15,23,42,.10);
  background:#111827;
  color:#fff;
  padding:12px 16px;
  border-radius:16px;
  font-weight:800;
  letter-spacing:-.01em;
  cursor:pointer;
  transition:transform .06s ease, box-shadow .16s ease, opacity .16s ease;
}
.btn:hover{
  box-shadow:0 14px 28px rgba(15,23,42,.12);
}
.btn:disabled{
  opacity:.52;
  cursor:not-allowed;
  box-shadow:none;
}
.btn--ghost{
  background:#fff;
  color:#0f172a;
}
.btn--primaryWide{
  width:100%;
}
.btn--primaryStrong{
  background:linear-gradient(180deg, #111827 0%, #0f172a 100%);
  box-shadow:0 18px 32px rgba(15,23,42,.18);
}

/* Workspace */
.workspace{
  display:grid;
  grid-template-columns:minmax(0, 1.45fr) minmax(320px, .82fr);
  gap:16px;
  align-items:start;
}

.workspaceMain,
.workspaceSide{
  display:flex;
  flex-direction:column;
  gap:14px;
}

/* Active card */
.activeCard{
  position:relative;
  overflow:hidden;
}
.activeCard:before{
  content:"";
  position:absolute;
  inset:0 auto 0 0;
  width:4px;
  background:rgba(59,130,246,.35);
}
.activeCard--order:before{
  background:rgba(16,185,129,.45);
}
.activeCard--docs:before,
.activeCard--sign:before{
  background:rgba(245,158,11,.45);
}
.activeCard--done:before{
  background:rgba(34,197,94,.50);
}
.activeCardTop{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:16px;
  flex-wrap:wrap;
}
.activeTitle{
  margin-top:4px;
  font-size:28px;
  line-height:1.08;
  font-weight:800;
  letter-spacing:-.03em;
}
.activeStatus{
  display:flex;
  align-items:center;
}
.focusTag{
  display:inline-flex;
  align-items:center;
  border-radius:999px;
  padding:8px 12px;
  font-size:12px;
  font-weight:800;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(148,163,184,.12);
  color:#334155;
}
.focusTag--version,
.focusTag--quote,
.focusTag--sent{
  background:rgba(59,130,246,.10);
  border-color:rgba(59,130,246,.22);
  color:#1e3a8a;
}
.focusTag--sign{
  background:rgba(245,158,11,.10);
  border-color:rgba(245,158,11,.20);
  color:#92400e;
}
.focusTag--order,
.focusTag--done{
  background:rgba(16,185,129,.10);
  border-color:rgba(16,185,129,.22);
  color:#065f46;
}
.activeMeta{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:center;
  margin-top:14px;
}
.actionStrip{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:center;
  margin-top:16px;
}

/* Pills */
.summaryPills,
.statusRow,
.actionRow,
.versionHeaderActions{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
  align-items:center;
}
.summaryPills--top{
  margin-top:14px;
}
.softPill,
.metaTag{
  display:inline-flex;
  align-items:center;
  gap:8px;
  border-radius:999px;
  padding:8px 12px;
  font-size:12px;
  font-weight:700;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(15,23,42,.04);
}
.softPill--warn{
  background:rgba(245,158,11,.10);
  border-color:rgba(245,158,11,.18);
  color:#92400e;
}
.softPill--good{
  background:rgba(16,185,129,.10);
  border-color:rgba(16,185,129,.18);
  color:#065f46;
}
.metaTag--danger{
  background:rgba(239,68,68,.10);
  border-color:rgba(239,68,68,.18);
  color:#991b1b;
}

.pill{
  display:inline-flex;
  align-items:center;
  gap:10px;
  padding:9px 12px;
  border-radius:999px;
  border:1px solid rgba(15,23,42,.10);
  background:#fff;
}
.pillValue{
  font-weight:800;
}
.pill .dot{
  width:10px;
  height:10px;
  border-radius:999px;
  background:#9ca3af;
}
.pill--good{
  border-color:rgba(16,185,129,.25);
  background:rgba(16,185,129,.10);
}
.pill--good .dot{ background:#10b981; }
.pill--warn{
  border-color:rgba(245,158,11,.25);
  background:rgba(245,158,11,.10);
}
.pill--warn .dot{ background:#f59e0b; }
.pill--bad{
  border-color:rgba(239,68,68,.25);
  background:rgba(239,68,68,.10);
}
.pill--bad .dot{ background:#ef4444; }
.pill--neutral{
  border-color:rgba(148,163,184,.26);
  background:rgba(148,163,184,.12);
}
.pill--neutral .dot{ background:#94a3b8; }

.helperLine{
  margin-top:12px;
  font-size:13px;
  line-height:1.55;
  color:#64748b;
}
.helperLine--good{
  color:#065f46;
}

/* Side cards */
.sideCard{
  padding:18px;
}
.sideTitle{
  font-size:20px;
}
.sideSub{
  margin-top:8px;
}
.sideMeta{
  margin-top:14px;
  display:grid;
  gap:12px;
}
.sideMetaItem{
  border:1px solid rgba(15,23,42,.07);
  background:#fbfdff;
  border-radius:18px;
  padding:12px 14px;
}
.sideMetaLabel{
  font-size:11px;
  text-transform:uppercase;
  letter-spacing:.05em;
  font-weight:800;
  color:#64748b;
}
.sideMetaValue,
.sideMetaText{
  margin-top:8px;
}
.sideMetaText{
  font-size:14px;
  font-weight:700;
  color:#0f172a;
}
.sideCard--highlight{
  box-shadow:0 0 0 1px rgba(16,185,129,.18), 0 22px 44px rgba(16,185,129,.10);
  border-color:rgba(16,185,129,.20);
}

/* Version selector */
.versionRail{
  display:flex;
  gap:12px;
  overflow-x:auto;
  padding-bottom:4px;
  margin-bottom:16px;
}
.versionPill{
  min-width:170px;
  text-align:left;
  border:1px solid rgba(15,23,42,.08);
  background:#fff;
  border-radius:20px;
  padding:14px;
  cursor:pointer;
  transition:transform .08s ease, box-shadow .15s ease, border-color .15s ease;
}
.versionPill:hover{
  transform:translateY(-1px);
  box-shadow:0 14px 28px rgba(15,23,42,.08);
}
.versionPill--active{
  border-color:rgba(59,130,246,.25);
  background:rgba(59,130,246,.07);
  box-shadow:0 12px 28px rgba(59,130,246,.10);
}
.versionPill--signed{
  border-color:rgba(16,185,129,.18);
}
.versionPill--sent{
  border-color:rgba(59,130,246,.15);
}
.versionPillTop{
  display:flex;
  justify-content:space-between;
  gap:10px;
  align-items:center;
}
.versionPillNum{
  font-size:16px;
  font-weight:800;
  color:#0f172a;
}
.versionPillStatus{
  font-size:11px;
  font-weight:800;
  color:#64748b;
  text-transform:uppercase;
  letter-spacing:.04em;
}
.versionPillSub{
  margin-top:8px;
  font-size:12px;
  color:#64748b;
}

.versionCardPro{
  border:1px solid rgba(15,23,42,.08);
  border-radius:22px;
  padding:18px;
  background:linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,.95));
}
.versionCardHead{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:16px;
  flex-wrap:wrap;
}
.versionHeroRow{
  display:flex;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
}
.versionNum{
  font-size:28px;
  font-weight:800;
  letter-spacing:-.03em;
}

.badge{
  display:inline-flex;
  align-items:center;
  padding:8px 12px;
  border-radius:999px;
  border:1px solid rgba(15,23,42,.08);
  font-weight:800;
  font-size:12px;
}
.badge--good{
  background:rgba(16,185,129,.10);
  border-color:rgba(16,185,129,.22);
  color:#065f46;
}
.badge--info{
  background:rgba(59,130,246,.10);
  border-color:rgba(59,130,246,.22);
  color:#1e3a8a;
}
.badge--neutral{
  background:rgba(148,163,184,.14);
  border-color:rgba(148,163,184,.24);
  color:#334155;
}
.miniTag--info{
  background:rgba(59,130,246,.10);
  border-color:rgba(59,130,246,.22);
  color:#1e3a8a;
}

.miniTag--warn{
  background:rgba(245,158,11,.10);
  border-color:rgba(245,158,11,.22);
  color:#92400e;
}
.timeline{
  display:grid;
  grid-template-columns:repeat(3, minmax(0, 1fr));
  gap:12px;
  margin-top:18px;
}
.timeline--pro{
  grid-template-columns:repeat(4, minmax(0, 1fr));
}
.timelineItem{
  display:flex;
  gap:12px;
  padding:14px;
  border-radius:20px;
  border:1px solid rgba(15,23,42,.08);
  background:#fbfdff;
}
.tlDot{
  width:12px;
  height:12px;
  border-radius:999px;
  margin-top:5px;
  background:#cbd5e1;
  box-shadow:inset 0 0 0 3px rgba(255,255,255,.95);
  border:1px solid rgba(15,23,42,.08);
  flex:0 0 auto;
}
.tlDot.on{
  background:#22c55e;
  border-color:rgba(34,197,94,.30);
}
.tlTitle{
  font-weight:800;
}
.tlSub{
  margin-top:4px;
  color:#64748b;
  font-size:12px;
}

/* Sections */
.section{
  margin-top:18px;
}
.sectionCard{
  border:1px solid rgba(15,23,42,.08);
  border-radius:22px;
  padding:18px;
  background:linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,.95));
}
.sectionCard--signed{
  scroll-margin-top:20px;
}
.sectionHead{
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  gap:16px;
  flex-wrap:wrap;
  margin-bottom:14px;
}
.inlineAction{
  display:flex;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
}
.infoStrip{
  border:1px solid rgba(59,130,246,.10);
  background:rgba(59,130,246,.06);
  border-radius:18px;
  padding:14px 16px;
  margin-bottom:14px;
}
.infoTitle{
  font-weight:800;
  color:#1d4ed8;
  margin-bottom:4px;
}
.infoText{
  font-size:13px;
  line-height:1.55;
  color:#334155;
}
.formSection + .formSection{
  margin-top:18px;
}
.formSectionTitle{
  font-size:14px;
  font-weight:800;
  color:#0f172a;
  margin-bottom:12px;
}
.sectionActions{
  display:flex;
  justify-content:flex-end;
  gap:10px;
  flex-wrap:wrap;
  margin-top:16px;
}
.sectionActions--top{
  margin-top:0;
}
.collapsedHint{
  padding:14px 16px;
  border:1px dashed rgba(15,23,42,.12);
  border-radius:18px;
  color:#64748b;
  background:#fbfdff;
}

.grid2{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px;
  margin-top:18px;
}
.stack{
  display:flex;
  flex-direction:column;
  gap:12px;
}
.statusLine{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}
.miniTag{
  font-size:12px;
  padding:7px 11px;
  border-radius:999px;
  border:1px solid rgba(15,23,42,.08);
  font-weight:800;
}
.miniTag--ok{
  background:rgba(16,185,129,.10);
  border-color:rgba(16,185,129,.22);
  color:#065f46;
}
.miniTag--muted{
  background:rgba(148,163,184,.14);
  border-color:rgba(148,163,184,.26);
  color:#334155;
}

.summaryBox{
  border:1px solid rgba(15,23,42,.08);
  background:#fbfdff;
  border-radius:18px;
  padding:14px;
  line-height:1.7;
  font-size:13px;
  color:#334155;
}

.multiSectionGrid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px;
}
.multiCard{
  border:1px solid rgba(15,23,42,.08);
  background:#fff;
  border-radius:18px;
  padding:14px;
}
.multiCardTitle{
  font-size:13px;
  font-weight:800;
  color:#0f172a;
  margin-bottom:10px;
}
.checkGrid{
  display:grid;
  grid-template-columns:1fr;
  gap:10px;
}
.checkItem{
  display:flex;
  align-items:center;
  gap:10px;
  font-size:14px;
  color:#334155;
  cursor:pointer;
}
.checkItem input{
  width:16px;
  height:16px;
  accent-color:#111827;
}

.sendRow{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:14px;
  flex-wrap:wrap;
}

.orderInputs{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
  margin:12px 0;
}
.orderInputs--side{
  grid-template-columns:1fr;
}
.finalBox{
  grid-column:1 / -1;
  border-radius:18px;
  padding:14px 16px;
  background:#f8fafc;
  border:1px solid rgba(15,23,42,.06);
}
.finalLabel{
  font-size:12px;
  font-weight:800;
  color:#64748b;
}
.finalValue{
  margin-top:6px;
  font-size:24px;
  font-weight:800;
}

.emptyCard{
  text-align:center;
  padding:28px;
}
.emptyCardTitle{
  font-size:18px;
  font-weight:800;
  margin-bottom:4px;
}
.emptyCardActions{
  margin-top:14px;
  display:flex;
  justify-content:center;
}

.mono{
  font-variant-numeric:tabular-nums;
}

@media (max-width: 1240px){
  .workspace{
    grid-template-columns:1fr;
  }
  .pipelineStats{
    min-width:0;
    width:100%;
  }
}

@media (max-width: 1100px){
  .pipelineRow{
    grid-template-columns:repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 920px){
  .page{
    padding:16px;
  }

  .pipelineTitle{
    font-size:28px;
  }

  .pipelineStats,
  .pickerGrid,
  .cleanGrid--two,
  .grid2,
  .orderInputs,
  .timeline,
  .timeline--pro,
  .multiSectionGrid{
    grid-template-columns:1fr;
  }

  .pipelineRow{
    display:flex;
    gap:12px;
    overflow-x:auto;
    padding-bottom:4px;
  }

  .pipeStep{
    min-width:220px;
  }

  .pipeConnector{
    display:none;
  }

  .inlineAction{
    flex-direction:column;
    align-items:stretch;
  }

  .inp--full{
    width:100%;
  }

  .versionPill{
    min-width:150px;
  }

  .activeTitle{
    font-size:24px;
  }
}
</style>